/**
 * Initial-state constructors for the L2 event-sourced store (MathToolImplementation.md §6.2).
 *
 * The store starts from a well-defined empty `State` and is driven forward
 * exclusively by `Event`s through the pure reducer. The only thing seeded ahead
 * of the event log is the integrity registry: it is pre-populated with one
 * `SystemClaim` per layer at the *honest defaults* (Machine `unverified`,
 * Expert `not-reviewed`, Community `none`), because the §6.2 `Event` union has no
 * "add a System Claim" event — every layer's check is registered up front and
 * the integrity stream then pushes *status records* downward into it
 * (Architecture_V2.01.md §7; INV-DEP keeps the direction: read-from-below only).
 */
import { SEEDED_SYSTEM_CLAIMS } from './integrity.js';
/** Build a `ReadonlyMap` from entries and freeze the carrier object. */
function frozenMap(entries) {
    return Object.freeze(new Map(entries));
}
/**
 * The integrity registry as a map, keyed by `SystemClaim.id`. Exposed so callers
 * (and tests) can seed a custom registry — e.g. a config-relative System Claim —
 * before replaying an event log.
 */
export function integrityRegistry(claims = SEEDED_SYSTEM_CLAIMS) {
    return frozenMap(claims.map((sc) => [sc.id, sc]));
}
/**
 * A completely empty store state with NO seeded integrity claims. Useful when a
 * caller wants to control the integrity registry entirely (tests, mocks).
 */
export function emptyState(integrity = frozenMap([])) {
    return Object.freeze({
        statusMap: frozenMap([]),
        constraints: frozenMap([]),
        claims: frozenMap([]),
        edges: Object.freeze([]),
        integrity,
        history: Object.freeze([]),
        flags: Object.freeze({}),
        modules: frozenMap([]),
        communityFlags: frozenMap([]),
    });
}
/**
 * The canonical Phase 1 initial state: empty registries, but the integrity
 * registry pre-seeded with one System Claim per layer at honest defaults. This
 * is the state to `replay(SEED_LOG)` against to reach the §9.1 base state.
 */
export function createInitialState(systemClaims = SEEDED_SYSTEM_CLAIMS) {
    return emptyState(integrityRegistry(systemClaims));
}
//# sourceMappingURL=state.js.map